import { memo, useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactElement, type ReactNode } from 'react';
import { useBrowserLayoutEffect } from '../use-browser-layout-effect';
import type { TreeNode } from '../api';
import { AnimalBack, animalCrown, animalMouth } from './AnimalBack';
import { PeopleLine } from './PeopleLine';
import { LetterShapes } from '../components/LetterGlyph';
import { CANOPY_NORM, lampAlpha } from './lights';
import { rng } from './seed';
import { ground } from './ground';
import { layoutLabel, type LabelBox } from './label';
import { track } from '../../../../../shared-web/track';
import { mascotSvg, type MascotFace } from '../../../../../shared/ember-mascot.js';
import { EMBER_CEREMONY_POSE, EMBER_LETTERS_POSE, EMBER_MOVE_MS, EMBER_TREE_FACES, useEmberWander } from './ember-wander';
import { letterPerch, letterPile } from './letter-pile';
import '../../../../../shared/ember-mascot.css';
import './label.css';
import './BanyanScene.css';

/**
 * The Banyan (v4.0 §4.17): an old tree at dusk, seen from behind the person
 * sitting under it. The lights in the canopy are life nodes — each one a
 * situation many people have stood in; the small moving points are fireflies
 * (atmosphere, never data). Everything here is drawn procedurally from a
 * fixed seed so the server and the browser paint the same tree.
 *
 * Scene units: a 560×600 box (a phone's width), widened to 1500 on a wide
 * screen by extending the sky and the outer canopy — the tree itself and
 * every light keep their coordinates. The canopy is an ellipse centred
 * (280, 230) with radii (250, 170); a node's normalized (x, y) from the
 * server (its fixed place, assigned once next to its nearest neighbour) maps
 * into it. The ground line is the scene's bottom edge (y=600); the viewer
 * sits on it at x=280. The ground itself is not drawn here: the page lays a
 * `.banyan-ground` band directly under the scene, sized by what stands on it
 * (the door, a caption), so text never climbs onto the figure.
 *
 * The scene is two SVGs in one box, same viewBox, same pan: the still tree
 * (sky, foliage, trunk, roots, the viewer) and, over it, the live layer
 * (fireflies, lights, the arriving light, the open light's tag). The live
 * layer is its own compositor layer, so a firefly's flicker or a light's
 * glide repaints a few circles over transparency — never the tree (2026-09-06:
 * as one SVG, every ambient frame redrew all 393 leaves; idle CPU 48%, taps
 * at 32fps). Fireflies animate opacity only: a transform on an SVG child
 * invalidates the whole SVG's paint.
 *
 * The homepage's scene keeps the hour (`clock`, 2026-10-26 founder, mock B):
 * the chat's two skins on the same clock — Sand by day, Night from 20:00 to
 * 06:00 — read from the document's `data-skin` (set before the first paint by
 * index.html, kept by TreeHome), so the server's tree and the browser's are
 * the same markup and only the colours differ. Its sky is the box's own
 * background with a sun or a moon and stars in a layer of its own, and two
 * hills stand behind the tree with the Chats list's cabins on them — smoke
 * from their chimneys by day, their windows lit at night; the aerial roots are
 * left out there (2026-10-27: across the ridges they read as a net). Without `clock`
 * (the ceremony, a conversation's arrival) the tree stays at dusk.
 *
 * `preserveAspectRatio="xMidYMax slice"` keeps the ground line and the person
 * on screen at every width. A narrow screen does not crop the canopy: the
 * tree is laid out narrower to the visible width (a taller, narrower banyan),
 * so every light in the window is on screen without dragging. The narrowing
 * is a layout, not a paint transform: every x is drawn toward the viewer's
 * column by `squash`, and every shape — a leaf, a light, a root's stroke —
 * keeps its own size (2026-09-06: as a `scale(squash, 1)` on the group, a
 * phone's lights were upright ellipses and its roots hairlines).
 */

// wideW (2026-09-06: 1120 → 1500): a desktop box is about 2.7:1, and `slice`
// anchored to the ground cropped whatever the width could not cover — at
// 1120 the top third of the scene, the canopy's crown and every light in it,
// under the nav or off screen. At 1500 the width covers the box and only sky
// is cropped, so the whole tree stands in the frame.
export const SCENE = Object.freeze({ w: 560, wideW: 1500, h: 600, ground: 600, viewerX: 280 });
// The lights' ellipse: where the window's lights hang, inside the foliage's
// main mass — one per scene. The phone's fills the crown's upper dome
// (2026-09-06 second pass: at cy 290 / ry 130 the dark dome above the highest
// light was twice the margin under the lowest, and the lights read as sitting
// low in the tree). The wide scene's is twice as wide — the canopy grew to
// 1500 units on 2026-09-06 and the lights had not: 40 lights hung in a
// 450-unit swarm at the top centre of a 1500-unit crown, the rest of it dark —
// and a little lower, so its top clears the floating nav on the desktop's
// ground-anchored crop. Neither reaches the outermost leaf groups: the lit
// tree fades into dark wings, centred on the trunk where the viewer stands.
const LIGHTS = Object.freeze({
  phone: Object.freeze({ cx: 280, cy: 275, rx: 225, ry: 135 }),
  wide: Object.freeze({ cx: 280, cy: 300, rx: 440, ry: 125 }),
});
/** How long a tag that has been handed over takes to fade out (label.css keeps the same length). */
const TAG_FADE_MS = 420;
/**
 * Where the signpost stands (BanyanSceneProps.signpost): on a wide screen at
 * SIGNPOST_X, in scene units before the narrowing — well left of the trunk's
 * foot (170) and of the furthest Ember comes out on that side (120). A phone's
 * strip does not reach that far, so there it stands at the strip's left edge,
 * its centre SIGNPOST_HALF_PX in, whole on screen.
 */
const SIGNPOST_X = -40;
const SIGNPOST_HALF_PX = 72;

const lightsEllipse = (wide: boolean): { cx: number; cy: number; rx: number; ry: number } => (wide ? LIGHTS.wide : LIGHTS.phone);

export function sceneNodePosition(node: { x?: number; y?: number }, wide = false): { x: number; y: number } {
  const c = lightsEllipse(wide);
  const nx = typeof node.x === 'number' ? node.x : CANOPY_NORM.cx;
  const ny = typeof node.y === 'number' ? node.y : CANOPY_NORM.cy;
  return {
    x: c.cx + ((nx - CANOPY_NORM.cx) / CANOPY_NORM.rx) * c.rx,
    y: c.cy + ((ny - CANOPY_NORM.cy) / CANOPY_NORM.ry) * c.ry,
  };
}

const BLOBS: Array<[number, number, number, number]> = [
  [280, 280, 250, 150], [80, 330, 150, 95], [480, 320, 150, 100], [190, 200, 160, 90],
  [370, 195, 160, 90], [280, 145, 130, 58], [10, 250, 120, 70], [550, 245, 120, 70],
];
// The wide screen's extra canopy: the same tree seen with more of its spread.
const WIDE_BLOBS: Array<[number, number, number, number]> = [
  [-120, 300, 170, 90], [-230, 350, 130, 70], [680, 295, 170, 90], [790, 345, 130, 70],
  [-60, 220, 130, 70], [620, 215, 130, 70],
  [-360, 330, 150, 80], [-460, 375, 110, 60], [920, 325, 150, 80], [1020, 370, 110, 60],
];

interface Leaf { x: number; y: number; rx: number; ry: number; shade: number }
/** An aerial root: from (x, top) to the ground, bowing by wob; w is its stroke. */
interface Root { x: number; top: number; wob: number; w: number }
interface Firefly { x: number; y: number; delay: number; dur: number }

function buildFoliage(blobs: Array<[number, number, number, number]>, rootXs: number[], seed: number): { leaves: Leaf[]; roots: Root[]; fireflies: Firefly[] } {
  const r = rng(3 + seed);
  const leaves: Leaf[] = [];
  for (const [cx, cy, rx, ry] of blobs) {
    for (let i = 0; i < 28; i += 1) {
      const a = r() * Math.PI * 2;
      const d = Math.sqrt(r());
      const rr = 24 + r() * 32;
      leaves.push({ x: cx + Math.cos(a) * rx * d, y: cy + Math.sin(a) * ry * d, rx: rr, ry: rr * 0.72, shade: Math.floor(r() * 3) });
    }
  }
  const roots: Root[] = [];
  const rr2 = rng(11 + seed);
  rootXs.forEach((x, i) => {
    const top = 380 + rr2() * 50;
    const w = 1.2 + rr2() * 2.6;
    const wob = (rr2() - 0.5) * 20;
    roots.push({ x, top, wob, w });
    if (i % 3 === 0) roots.push({ x: x + 7, top: top + 20, wob, w: w * 0.5 });
  });
  const fireflies: Firefly[] = [];
  const fr = rng(99 + seed);
  const minX = Math.min(...blobs.map((b) => b[0] - b[2]));
  const maxX = Math.max(...blobs.map((b) => b[0] + b[2]));
  const within = (x: number, y: number): boolean => blobs.some(([cx, cy, rx, ry]) => ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1);
  // Density: 20 per scene width (28 on a phone), capped at 45 on a wide screen
  // (the 2026-09-06 perf figure; the 1500-unit scene would otherwise ask for
  // 60) — each one is an animation ticking every frame.
  const want = Math.min(45, Math.round(20 * ((maxX - minX) / SCENE.w)));
  let guard = 0;
  while (fireflies.length < want && guard < 4000) {
    guard += 1;
    const x = minX + fr() * (maxX - minX);
    const y = 90 + fr() * 400;
    if (within(x, y)) fireflies.push({ x, y, delay: fr() * 6, dur: 3.2 + fr() * 3 });
  }
  return { leaves, roots, fireflies };
}

/** The two hills behind the tree (clock): each ridge's height at x, rolling enough to read as hills, the same on every screen. */
const RIDGE_FAR = (x: number): number => 438 + 15 * Math.sin(x / 160 + 1.3) + 7 * Math.sin(x / 63 + 0.4);
const RIDGE_NEAR = (x: number): number => 500 + 13 * Math.sin(x / 125 + 2.2) + 5 * Math.sin(x / 49 + 1.1);
function hillPath(ridge: (x: number) => number, x0: number, x1: number): string {
  let d = `M${x0} ${ridge(x0).toFixed(1)}`;
  for (let x = x0 + 16; x < x1 + 16; x += 16) d += ` L${x} ${ridge(x).toFixed(1)}`;
  return `${d} L${x1 + 16} ${SCENE.ground + 20} L${x0} ${SCENE.ground + 20} Z`;
}
/**
 * A hill (clock), drawn soft (2026-10-27 founder: two hard edges read as two lines across the screen): its body is
 * paler at the crest and deepens toward its foot (`fill`, a gradient), and its ridge is feathered by two fainter
 * copies stepped 3 and 6 above it — a distant shape, not a line, with no blur to recompute while the tree pans.
 */
function HillShape({ d, fill }: { d: string; fill: string }): ReactElement {
  return (
    <g>
      <path d={d} fill={fill} opacity=".16" transform="translate(0 -6)" />
      <path d={d} fill={fill} opacity=".38" transform="translate(0 -3)" />
      <path d={d} fill={fill} />
    </g>
  );
}
/**
 * The cabins on the hills (clock): the Chats list's cabin, at a scene x before the narrowing (so a phone keeps them
 * at its edges), `s` its size, `near` on the nearer hill. The wide scene has a few more out along its wings.
 */
interface Cabin { x: number; s: number; near: boolean }
const CABINS: Cabin[] = [{ x: 28, s: 0.62, near: false }, { x: 522, s: 0.56, near: false }, { x: 470, s: 0.8, near: true }];
const CABINS_WIDE: Cabin[] = [
  { x: 28, s: 0.8, near: false }, { x: 522, s: 0.74, near: false }, { x: 470, s: 1.05, near: true },
  { x: -330, s: 0.78, near: false }, { x: -175, s: 1, near: true }, { x: 770, s: 0.96, near: true }, { x: 935, s: 0.76, near: false },
];
/** The path up to the near cabin (CABINS[2]). */
const PATH_CABIN = 2;
/** The Chats list's cabin (Chats.tsx CABIN, its own 30×30 box) with its top-left corner at (left, top), at size s. */
function CabinShape({ c }: { c: { left: number; top: number; s: number } }): ReactElement {
  return (
    <g transform={`translate(${c.left.toFixed(1)} ${c.top.toFixed(1)}) scale(${c.s})`}>
      <rect className="banyan-cabin-wall" x="3" y="12" width="24" height="17" />
      <rect className="banyan-cabin-roof" x="21" y="2" width="3.5" height="8" />
      <path className="banyan-cabin-roof" d="M0 13 L15 0.5 L30 13 Z" />
      <rect className="banyan-cabin-window" x="11" y="17" width="7" height="7" rx="1.2" />
    </g>
  );
}
/** The stars (clock, at night): fixed points across the wide sky; the canopy hides most of them, as it would. */
const STARS: Array<[number, number, number]> = [
  [-420, 60, 1.2], [-300, 120, 0.9], [-220, 40, 1.4], [-130, 96, 1], [-40, 30, 1.1], [30, 70, 1.3], [96, 24, 0.9], [150, 58, 1],
  [380, 40, 1.2], [430, 70, 0.9], [500, 30, 1.4], [540, 160, 1], [610, 60, 1.1], [690, 110, 0.9], [760, 36, 1.3], [880, 150, 1],
  [960, 70, 1.2], [1010, 130, 0.9],
];
/** The clouds (clock, by day): a cloud is three puffs on a rounded base, at (x, y) and size k. */
const CLOUDS: Array<[number, number, number]> = [[108, 104, 0.8], [404, 96, 0.7], [-230, 92, 1.1], [905, 168, 0.9]];
/** The sun and the moon: where a phone's crown leaves sky (left of its top, under the wordmark), and upper right of a wide screen's. */
const ORB = Object.freeze({ phone: { x: 190, y: 66 }, wide: { x: 800, y: 132 } });
/** The Chats list's moon (Chats.tsx), on its 24-unit box. */
const MOON_PATH = 'M20.5 14.2A8.6 8.6 0 1 1 9.8 3.5a7 7 0 0 0 10.7 10.7z';

const ROOT_XS = [70, 118, 160, 200, 236, 330, 366, 402, 446, 494];
const FOLIAGE = buildFoliage(BLOBS, ROOT_XS, 0);
const FOLIAGE_WIDE = buildFoliage([...BLOBS, ...WIDE_BLOBS], [...ROOT_XS, -180, -110, -40, 610, 690, 760, -330, -410, 880, 970], 7);
const SHADES = ['var(--banyan-canopy-1)', 'var(--banyan-canopy-2)', 'var(--banyan-canopy-3)'];

/** The post-telling ceremony (§4.17): one light arriving from the viewer's
 *  head at a spot in the canopy. `hold`: nothing yet; `speaking`: the teller
 *  is still telling — a speech bubble at their mouth, the head wagging, while a light
 *  gathers there; `flying`: the spark is on its way; `lit`: the new light
 *  stands there, ringed as the viewer's. */
export interface Arrival {
  x: number;
  y: number;
  stage: 'hold' | 'speaking' | 'flying' | 'lit';
  /**
   * How fast the ceremony runs: every duration (the bubble, the chatter, the
   * gathering light, the flight, the landing) is multiplied by it. 1 is the
   * full-screen ceremony on the Result page; the thread's arrival over a
   * conversation (2026-09-12) runs at a fraction of it, two seconds in all.
   */
  tempo?: number;
}

/**
 * Where the arriving light lands: a spot in the upper canopy, clear of the
 * lights already there. Random on purpose — the ceremony says "your telling
 * is a light on this tree", not where; the seals under the title say where.
 */
export function pickArrivalSpot(nodes: TreeNode[], wide = false): { x: number; y: number } {
  const c = lightsEllipse(wide);
  const taken = nodes.filter((n) => typeof n.x === 'number' && typeof n.y === 'number').map((n) => sceneNodePosition(n, wide));
  let spot: { x: number; y: number } = { x: c.cx, y: c.cy - 40 };
  for (let i = 0; i < 40; i += 1) {
    const a = Math.random() * Math.PI * 2;
    const d = 0.25 + Math.sqrt(Math.random()) * 0.55;
    spot = { x: c.cx + Math.cos(a) * c.rx * d, y: c.cy + Math.sin(a) * c.ry * d };
    if (spot.y > c.cy + c.ry * 0.35) continue;
    if (taken.every((t) => Math.hypot(t.x - spot.x, t.y - spot.y) >= 34)) break;
  }
  return spot;
}

/** A speech bubble: a rounded box with a tail from near its bottom-left
 *  corner to `tip` (the mouth). Box by its top-left corner, size and corner
 *  radius; the tail's base sits just past the corner's curve. */
function bubblePath(x0: number, y0: number, w: number, h: number, r: number, tip: { x: number; y: number }, tailW: number): string {
  const a = x0 + r - 4;
  return `M${a} ${y0 + h} L${tip.x} ${tip.y} L${a + tailW} ${y0 + h} H${x0 + w - r} a${r} ${r} 0 0 0 ${r} -${r} V${y0 + r} a${r} ${r} 0 0 0 -${r} -${r} H${x0 + r} a${r} ${r} 0 0 0 -${r} ${r} V${y0 + h - r} a${r} ${r} 0 0 0 ${r} ${r} Z`;
}

/** What the figure under the tree does when someone taps it (§4.1, 2026-09-06).
 *  It is in the middle of telling, or of listening, and would rather not be
 *  poked: each reaction is a small movement of the head (twice of the whole
 *  figure) with either a comic mark over the head or a few words at its mouth.
 *  One is drawn at random, never the same one twice running; `ms` is how long
 *  the reaction is on screen. The movements are in BanyanScene.css under
 *  `.is-poke-<id>`. */
const POKES: ReadonlyArray<{ id: string; ms: number; mark?: string; say?: string }> = [
  { id: 'startle', ms: 1500, mark: '!' },
  { id: 'hm', ms: 1700, mark: '?' },
  { id: 'hum', ms: 2200, mark: '\u266a' },
  { id: 'shh', ms: 1900, say: 'Shh. I\u2019m telling.' },
  { id: 'tickle', ms: 1900, say: 'Hey! That tickles.' },
  { id: 'busy', ms: 2100, say: 'Not now, it\u2019s my turn.' },
  { id: 'hmph', ms: 1800, say: 'Hmph.' },
  { id: 'listening', ms: 2100, say: 'Shh, I\u2019m listening.' },
];

/** The words a poked figure says, in a bubble at its mouth. The bubble is as
 *  wide as the words: measured before paint, so the box never shows at the
 *  wrong size. Type is set in screen pixels (`zoom`), like the tags. */
function PokeWords({ text, mouth, zoom, ms }: { text: string; mouth: { x: number; y: number }; zoom: number; ms: number }): ReactElement {
  const ref = useRef<SVGTextElement>(null);
  const [textW, setTextW] = useState(0);
  useBrowserLayoutEffect(() => { setTextW(ref.current?.getComputedTextLength() || 0); }, [text, zoom]);
  const h = 30 * zoom;
  const padX = 12 * zoom;
  const w = textW + padX * 2;
  const x0 = mouth.x + 22;
  const y0 = mouth.y - 16 - h;
  return (
    <g className="banyan-poke-bubble" style={{ ['--poke-ms' as string]: `${ms}ms` }}>
      <path className="banyan-poke-paper" d={bubblePath(x0, y0, w, h, h / 2, { x: mouth.x + 6, y: mouth.y }, 14 * zoom)} strokeWidth={(2 * zoom).toFixed(2)} strokeLinejoin="round" />
      <text ref={ref} className="banyan-poke-words" x={(x0 + padX).toFixed(1)} y={(y0 + h / 2).toFixed(1)} style={{ fontSize: `${(13 * zoom).toFixed(2)}px` }}>{text}</text>
    </g>
  );
}

/** `waiting`: someone is waiting on this person's answer to it (knock-letters isAwaited) — sealed, it brings Ember over (2026-09-29 拍板). `invite`: it is a room's invitation. */
export interface SceneLetter { id: string; sealed: boolean; fly: boolean; waiting?: boolean; invite?: boolean }

export interface BanyanSceneProps {
  nodes: TreeNode[];
  /** The viewer's animal; null paints no one (server render, or not known yet). */
  animal: string | null;
  /** The light whose card is open. Its neighbours brighten with distance. */
  highlightId?: string | null;
  /**
   * The light the tree is pressing by itself (2026-09-08): the demonstration's
   * tap. For a beat the light dips and a touch ripple runs out from it —
   * the scene performing the act the tag is the result of, in its own
   * language (a ring in lamp light, no hand, no words). The page hangs the
   * tag when the press is over.
   */
  pressId?: string | null;
  /** The open light's doors, hung as the third row of its tag: a
   *  `.banyan-label-actions` row of `.banyan-label-cta` / `.banyan-label-more`
   *  (the page owns what they do; the scene owns where they hang). */
  labelActions?: ReactNode;
  /**
   * A row hung ABOVE the tag's title (2026-09-08 拍板): the demonstration's
   * `Tap any light.` — a `.banyan-label-hint`. It is part of the tag, not a
   * caption near it: the same element fades it in and out with the title, the
   * count and the doors, and the ghost of a tag going out carries it too.
   */
  labelHint?: ReactNode;
  /** The post-telling ceremony (§4.17): one light arriving in the canopy. */
  arrival?: Arrival | null;
  /**
   * Ember's letters to the viewer (§4.9, 2026-09-06): one envelope per letter
   * waiting on them, on the ground beside the figure. `sealed` until the
   * person unfolds it in the sheet; `fly` on the visit it first arrives — it
   * drifts down from the canopy once, and every later look finds it landed.
   */
  letters?: SceneLetter[];
  /** A tap on the pile — the page opens the letters sheet. */
  onLettersTap?: () => void;
  onLightTap?: (node: TreeNode, anchor: { x: number; y: number }) => void;
  /** Called on any background tap / drag start — the card closes. */
  onBackgroundTap?: () => void;
  /**
   * Ember under the tree (拍板 2026-09-14, ember-wander.ts): drawn whenever
   * there is a figure. It wanders while `emberActive` (nothing else is open on
   * the scene) and holds still, facing you, while `emberHeld` (its card is
   * up). A tap on it calls `onEmberTap` with the point above its head in the
   * scene box's pixels — the page hangs the card there.
   */
  emberActive?: boolean;
  emberHeld?: boolean;
  onEmberTap?: (anchor: { x: number; y: number }) => void;
  /**
   * Something planted in the ground left of the trunk (2026-10-21 founder: the
   * homepage's sign to Chats). The page owns what it is and what it does; the
   * scene owns where it stands — its box's bottom centre on the ground, moving
   * with the ground when the tree is dragged. HTML over the scene like the
   * tags, so it is a real link and its word is set in screen pixels; Ember
   * walks behind it.
   */
  signpost?: ReactNode;
  /** The homepage's scene: it keeps the hour (Sand by day, Night at night), with the hills and their cabins. */
  clock?: boolean;
  /** Sideways drag to see more canopy on narrow screens. */
  pannable?: boolean;
  /** A wide screen: the scene extends sideways instead of being cropped. */
  wide?: boolean;
  className?: string;
}

function BanyanSceneImpl({
  nodes, animal, highlightId = null, pressId = null, labelActions = null, labelHint = null, arrival = null, letters = [], onLettersTap,
  onLightTap, onBackgroundTap, emberActive = true, emberHeld = false, onEmberTap, signpost = null, clock = false, pannable = true, wide = false, className = '',
}: BanyanSceneProps): ReactElement {
  const foliage = wide ? FOLIAGE_WIDE : FOLIAGE;
  const sceneW = wide ? SCENE.wideW : SCENE.w;
  const sceneX0 = wide ? -(SCENE.wideW - SCENE.w) / 2 : 0;
  const uid = useId().replace(/:/g, '');
  const ids = {
    sky: `${uid}-sky`, glow: `${uid}-glow`, rise: `${uid}-rise`, rim: `${uid}-rim`, trunk: `${uid}-trunk`, hz: `${uid}-hz`, seat: `${uid}-seat`, bank: `${uid}-bank`, ridge: `${uid}-ridge`,
  };
  // The earth the tree stands in (ground.ts): the scene's bottom edge given a
  // shape, wide enough to cover any pan. It pans with the trunk and roots it
  // covers, so a root stays planted where it went in.
  const earth = useMemo(() => ground(sceneX0 - 400, sceneX0 + sceneW + 400, SCENE.ground, 1, wide ? 5 : 3), [sceneX0, sceneW, wide]);
  // The hills behind the tree (clock), as wide as the earth.
  const hills = useMemo(() => (clock ? {
    far: hillPath(RIDGE_FAR, sceneX0 - 400, sceneX0 + sceneW + 400),
    near: hillPath(RIDGE_NEAR, sceneX0 - 400, sceneX0 + sceneW + 400),
  } : null), [clock, sceneX0, sceneW]);
  const svgRef = useRef<SVGSVGElement>(null);
  const [pan, setPan] = useState(0);
  // Horizontal squash of the tree (1 = as drawn). Set from the rendered box.
  const [squash, setSquash] = useState(1);
  // Scene→screen scale, so the label under a light is set in screen pixels
  // (the scene shrinks on a wide box and grows on a tall phone; type must not).
  const [zoom, setZoom] = useState(1);
  // The visible strip of the scene, in scene units: the tag under a light is
  // kept inside it, so a light near the edge still hangs a whole tag.
  // y0: the scene row at the box's top edge — xMidYMax slice pins the ground
  // and crops the scene from the top when the box is shorter than the scene.
  const [view, setView] = useState<{ x0: number; w: number; y0: number }>({ x0: sceneX0, w: sceneW, y0: 0 });
  const drag = useRef<{ startX: number; startPan: number; moved: boolean; scale: number; max: number } | null>(null);

  const placed = useMemo(() => nodes
    .filter((n) => typeof n.x === 'number' && typeof n.y === 'number')
    .map((n) => ({ node: n, ...sceneNodePosition(n, wide) })), [nodes, wide]);
  // The window re-grows when the node set changes: lights that left fade out
  // where they were for a moment, lights that arrived fade in, lights that
  // stayed glide to their new place (CSS transition on the group transform).
  const [leaving, setLeaving] = useState<Array<{ node: TreeNode; x: number; y: number }>>([]);
  const previous = useRef(placed);
  useEffect(() => {
    const next = new Set(placed.map((p) => p.node.id));
    const gone = previous.current.filter((p) => !next.has(p.node.id));
    previous.current = placed;
    if (gone.length === 0) return undefined;
    setLeaving(gone);
    const t = window.setTimeout(() => setLeaving([]), 520);
    return () => window.clearTimeout(t);
  }, [placed]);
  const highlight = placed.find((p) => p.node.id === highlightId) || null;
  // The tag going out (2026-09-07 拍板): a tag never vanishes — when the open
  // light changes or its tag is put away, the tag that was hanging fades out
  // where it hung. It is a ghost: a picture of the tag as it stood, doors and
  // all (2026-09-07 二次拍板 — the doors used to be left off it, so every fade
  // began with the pill popping out of the tag), drawn from the light's place
  // at the moment it was handed over, and nothing on it can be pressed.
  type Tag = { node: TreeNode; x: number; y: number; h: number; actions: ReactNode; hint: ReactNode };
  const [fadingTag, setFadingTag] = useState<Tag | null>(null);
  const lastTag = useRef<Tag | null>(null);
  useEffect(() => {
    const gone = lastTag.current;
    // labelH is still the height of the tag that is leaving: the layout effect
    // that measures the new one runs after this render, not during it. So the
    // ghost keeps its own height and does not re-centre itself mid-fade.
    lastTag.current = highlight ? { node: highlight.node, x: highlight.x, y: highlight.y, h: labelH, actions: labelActions, hint: labelHint } : null;
    if (!gone || gone.node.id === highlightId) return undefined;
    setFadingTag(gone);
    const t = window.setTimeout(() => setFadingTag(null), TAG_FADE_MS);
    return () => window.clearTimeout(t);
    // Only when the open light itself changes: `highlight` is a fresh object
    // every render, and re-running this each frame would cancel the fade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId]);
  // The tag's own height, in screen pixels (the tag is HTML; layoutLabel gets
  // it scaled into scene units), read before paint: the tag is centred on the
  // light it hangs beside, so a title that wrapped to two lines must not
  // settle a line lower a frame later.
  const labelRef = useRef<HTMLDivElement>(null);
  const [labelH, setLabelH] = useState(0);
  useBrowserLayoutEffect(() => {
    const h = labelRef.current?.offsetHeight || 0;
    setLabelH((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
    // Not on every render: a pan frame moves the tag, it never resizes it, and
    // reading offsetHeight mid-drag would force a layout each frame.
  }, [highlightId, labelActions, labelHint, zoom, view.w, view.x0]);

  // Scene→screen scale and the pan range, from the rendered box.
  const measure = (): { scale: number; max: number } => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box || box.height === 0) return { scale: 1, max: 0 };
    // slice: the scale is whichever axis must grow to cover the box.
    const scale = Math.max(box.width / sceneW, box.height / SCENE.h);
    const visibleW = box.width / scale;
    return { scale, max: Math.max(0, (sceneW * squash - visibleW) / 2) };
  };

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>): void => {
    if (!pannable) return;
    const m = measure();
    drag.current = { startX: e.clientX, startPan: pan, moved: false, ...m };
  };
  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>): void => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.scale;
    if (!d.moved && Math.abs(dx) > 4) {
      d.moved = true;
      // A drag is the tree's third act (2026-09-08), once per gesture: until
      // now a visit that only panned the canopy left no row at all, and a
      // homepage with 60% "no action" could not say how many of them had
      // their hands on the tree. Only where the tree is live (the ceremony's
      // backdrop cannot pan and opens nothing).
      if (onLightTap) track('ui.clicked', { name: 'tree_pan' });
      onBackgroundTap?.();
      svgRef.current?.setPointerCapture?.(e.pointerId);
    }
    if (d.moved) setPan(Math.max(-d.max, Math.min(d.max, d.startPan + dx)));
  };
  const endDrag = (): void => { drag.current = null; };

  // The figure under the tree, poked. One reaction at a time; a second tap
  // mid-reaction starts a different one (never the same twice running, so the
  // class always changes and the movement restarts). Cleared when it is over.
  // Not during the ceremony — there the figure is already speaking.
  const [poke, setPoke] = useState<{ id: string; seq: number } | null>(null);
  const pokeTimer = useRef(0);
  const lastPoke = useRef<string | null>(null);
  useEffect(() => () => window.clearTimeout(pokeTimer.current), []);
  const pokeYou = (): void => {
    const options = POKES.filter((p) => p.id !== lastPoke.current);
    const pick = options[Math.floor(Math.random() * options.length)];
    lastPoke.current = pick.id;
    track('ui.clicked', { name: 'tree_you_poke', state: pick.id });
    setPoke((prev) => ({ id: pick.id, seq: (prev?.seq ?? 0) + 1 }));
    window.clearTimeout(pokeTimer.current);
    pokeTimer.current = window.setTimeout(() => setPoke(null), pick.ms);
  };
  const reaction = poke ? POKES.find((p) => p.id === poke.id) || null : null;

  // Ember under the tree. On the landing it wanders (ember-wander.ts); in the
  // ceremony it sits at the figure's left shoulder and its face follows the
  // stage — listening while you speak, warm for its own short reply
  // (0.8–1.4s of the 2.2s speaking beat), thinking as the light climbs, warm
  // when it stands lit. The drawing is mounted once (the faces it can wear
  // here, EMBER_TREE_FACES) and its face is an attribute, like every other mascot.
  const emberOn = Boolean(animal);
  // A letter someone is waiting on lies sealed at the feet (2026-09-29 拍板):
  // Ember leaves its round, goes over and sits on the envelopes (letter-pile.ts letterPerch)
  // and sits there with one line above its head — "You have a letter." —
  // until the letter is opened. The wander's clock pauses meanwhile; a tap
  // on Ember or on the line opens the letters like a tap on the pile.
  const calling = emberOn && !arrival && letters.some((l) => l.sealed && l.waiting);
  // A room's invitation among them is said as one (2026-10-22 jam): the Chats list says "Invitation from Ember" and
  // the page "A room, if you want it" — "a letter" was a third name for the same thing.
  const invited = letters.some((l) => l.sealed && l.waiting && l.invite);
  const [arrivedAtLetters, setArrivedAtLetters] = useState(false);
  useEffect(() => {
    if (!calling) { setArrivedAtLetters(false); return undefined; }
    const t = window.setTimeout(() => setArrivedAtLetters(true), EMBER_MOVE_MS);
    return () => window.clearTimeout(t);
  }, [calling]);
  const { pose: wanderPose, firefly } = useEmberWander({ enabled: emberOn && !arrival, active: emberActive && !calling, held: emberHeld });
  const [ceremonyFace, setCeremonyFace] = useState<MascotFace>('listening');
  useEffect(() => {
    if (!arrival) return undefined;
    const tempo = arrival.tempo ?? 1;
    if (arrival.stage === 'speaking') {
      setCeremonyFace('listening');
      const a = window.setTimeout(() => setCeremonyFace('warm'), 800 * tempo);
      const b = window.setTimeout(() => setCeremonyFace('listening'), 1400 * tempo);
      return () => { window.clearTimeout(a); window.clearTimeout(b); };
    }
    setCeremonyFace(arrival.stage === 'flying' ? 'thinking' : arrival.stage === 'lit' ? 'warm' : 'listening');
    return undefined;
  }, [arrival?.stage, arrival?.tempo]); // eslint-disable-line react-hooks/exhaustive-deps
  const emberPose = arrival ? { ...EMBER_CEREMONY_POSE, face: ceremonyFace } : calling ? { ...EMBER_LETTERS_POSE, walking: !arrivedAtLetters } : wanderPose;
  const emberHtml = useMemo(() => mascotSvg({ tag: 'svg', width: 80, x: -40, y: -96, face: 'listening', faces: EMBER_TREE_FACES, uid: `${uid}-ember` }), [uid]);
  const emberHost = useRef<SVGGElement>(null);
  useEffect(() => {
    emberHost.current?.querySelector('.ember-mascot')?.setAttribute('data-face', emberPose.face);
  }, [emberPose.face]);

  useEffect(() => {
    // The tree fits the visible width: on a phone the scene is cropped at the
    // sides, so the canopy (and everything in it) is squashed to what shows.
    const fit = (): void => {
      const box = svgRef.current?.getBoundingClientRect();
      if (!box || box.height === 0) return;
      const scale = Math.max(box.width / sceneW, box.height / SCENE.h);
      const visibleW = box.width / scale;
      setSquash(Math.max(0.58, Math.min(1, (visibleW * 0.96) / sceneW)));
      setZoom(1 / scale);
      setView({ x0: sceneX0 + (sceneW - visibleW) / 2, w: visibleW, y0: Math.max(0, SCENE.h * scale - box.height) / scale });
      const { max } = measure();
      setPan((p) => Math.max(-max, Math.min(max, p)));
    };
    fit();
    // The box, not the window (2026-10-21): the ground band under the scene is
    // filled after the first paint (the home payload, a conversation's tail),
    // and the box shrinks with no window resize — the scale kept from the first
    // paint hung the tags, Ember's line and the signpost low, and narrowed the
    // tree for a taller box than the one on screen.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (svgRef.current) ro?.observe(svgRef.current);
    return () => ro?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneW]);

  // Opening a light is the tree's one act, recorded here (ui.clicked
  // tree_light_open with the node, no state) and nowhere else: a drag that
  // ended on a light is not a tap, so the declarative click hook would count
  // presses that opened nothing; the tag that then hangs is the same act's
  // exposure (ui.seen, in TreeHome). A scene with no handler — the
  // ceremony's backdrop — has nothing to open and records nothing.
  const openLight = (node: TreeNode, anchor: { x: number; y: number }): void => {
    if (!onLightTap) return;
    track('ui.clicked', { name: 'tree_light_open', node_id: node.id });
    onLightTap(node, anchor);
  };
  const tap = (p: { node: TreeNode; x: number; y: number }, e: ReactPointerEvent<SVGGElement>): void => {
    if (drag.current?.moved) return;
    e.stopPropagation();
    // The press is over: without this the next mouse movement would read as
    // a drag that started at the light, and close what it just opened.
    drag.current = null;
    const target = e.currentTarget.querySelector('.banyan-light-core') as SVGCircleElement | null;
    const box = (target || e.currentTarget).getBoundingClientRect();
    const host = svgRef.current?.parentElement?.getBoundingClientRect();
    openLight(p.node, {
      x: box.left + box.width / 2 - (host?.left || 0),
      y: box.top + box.height / 2 - (host?.top || 0),
    });
  };

  // The figure's size in scene units. A phone shows the scene smaller than a
  // desktop does, so the figure is a size larger there (2026-09-06 走查: on a
  // 390px phone the person under the tree was a 65px dark shape).
  const figure = wide ? 150 : 176;
  // The teller's mouth — the side of this animal's head (a giraffe's is far
  // higher than a hedgehog's) — is where the speech bubble's tail points; the
  // bubble hangs up and to the right of it, and the light gathers inside.
  const mouth = animalMouth(animal || '');
  const mouthX = SCENE.viewerX + mouth.dx * (figure / 200) - 2;
  const mouthY = SCENE.ground - mouth.up * (figure / 200) - 6;
  const bubbleX = mouthX + 58;
  const bubbleY = mouthY - 46;
  // …and the top of its head, where a comic mark stands when it is poked.
  const crown = animalCrown(animal || '');
  const crownX = SCENE.viewerX + crown.dx * (figure / 200);
  const crownY = SCENE.ground - crown.up * (figure / 200);
  // The spark's flight is SMIL (it follows the squashed canopy like every
  // light does); it is started from here so it begins when `flying` does,
  // not when the SVG happened to mount.
  const flightRef = useRef<SVGAnimateMotionElement>(null);
  // Before paint: the spark is shown the same render the light gathered at
  // the head is hidden, and must already stand on its path's first point.
  useBrowserLayoutEffect(() => {
    if (arrival?.stage === 'flying') flightRef.current?.beginElement?.();
  }, [arrival?.stage]);
  // The narrowing: a scene x drawn toward the viewer's column. Shapes are not
  // narrowed, only where they stand.
  const sx = (x: number): number => SCENE.viewerX + (x - SCENE.viewerX) * squash;
  // Scene → screen for the tags: layoutLabel works in scene units on the
  // visible strip; the tag is HTML positioned in .banyan-scene's pixels, and
  // that box is the live SVG's (xMidYMax slice: view.x0 / view.y0 are the
  // scene rows at its left and top edges, 1/zoom its scale).
  const toScreen = (b: LabelBox): LabelBox => ({ ...b, x: (b.x - view.x0) / zoom, y: (b.y - view.y0) / zoom, w: b.w / zoom });
  // The envelopes at the feet (letter-pile.ts), and — while a letter someone
  // is waiting on lies sealed there — Ember's seat on them. The pile keeps to
  // the figure and is not squashed, so Ember's seat is not either.
  const pile = letterPile(figure, letters.length, { viewerX: SCENE.viewerX, ground: SCENE.ground });
  const perch = letterPerch(pile);
  const emberAt = calling
    ? { x: perch.x, y: perch.y, scale: perch.w / 80, w: perch.w }
    : { x: sx(emberPose.x), y: SCENE.ground, scale: emberPose.w / 80, w: emberPose.w };
  // The cabins (clock): each where the narrowing puts it, standing on its ridge there; its chimney's top and its
  // window's middle are where the smoke rises from and the night's light falls from (the live layer).
  const cabins = clock ? (wide ? CABINS_WIDE : CABINS).map((c) => {
    const x = sx(c.x);
    const y = (c.near ? RIDGE_NEAR : RIDGE_FAR)(x) + 1;
    const left = x - 15 * c.s;
    const top = y - 29 * c.s;
    return { ...c, x, y, left, top, chimney: { x: left + 22.75 * c.s, y: top + 2 * c.s }, window: { x: left + 14.5 * c.s, y: top + 20.5 * c.s } };
  }) : [];
  // The path up to the near cabin: a ribbon winding up the near hill from the ground in front, wide at its foot (under
  // the near bank) and narrowing to the cabin.
  const walk = cabins[PATH_CABIN] ? (() => {
    const t = { x: cabins[PATH_CABIN].x + 2 * cabins[PATH_CABIN].s, y: cabins[PATH_CABIN].y - 0.5 };
    const f = { x: t.x - 30, y: SCENE.ground + 4 };
    const n = (v: number): string => v.toFixed(1);
    return `M${n(f.x - 26)} ${f.y} C ${n(f.x - 20)} ${f.y - 30}, ${n(t.x + 26)} ${n(t.y + 40)}, ${n(t.x - 4)} ${n(t.y)} L ${n(t.x + 4)} ${n(t.y)} C ${n(t.x + 40)} ${n(t.y + 40)}, ${n(f.x + 14)} ${f.y - 30}, ${n(f.x + 26)} ${f.y} Z`;
  })() : null;
  const orb = wide ? ORB.wide : ORB.phone;
  const root = (r: Root): string => `M${sx(r.x).toFixed(1)} ${r.top.toFixed(1)} C ${(sx(r.x) + r.wob).toFixed(1)} ${(r.top + 100).toFixed(1)}, ${(sx(r.x) - r.wob).toFixed(1)} ${(r.top + 200).toFixed(1)}, ${(sx(r.x) + r.wob * 0.4).toFixed(1)} ${SCENE.ground}`;
  const trunk = `M235 ${SCENE.ground - 230} C 245 ${SCENE.ground - 160}, 235 ${SCENE.ground - 80}, 170 ${SCENE.ground} L 390 ${SCENE.ground} C 325 ${SCENE.ground - 80}, 317 ${SCENE.ground - 160}, 327 ${SCENE.ground - 230} Z`;

  const viewBox = `${sceneX0} 0 ${sceneW} ${SCENE.h}`;
  const panStyle = { transform: `translate(${pan}px, 0)` };
  // The lower trunk again, for Ember to hide behind (see the layers below).
  const trunkLayer = (
    <svg className="banyan-trunk-layer" viewBox={viewBox} preserveAspectRatio="xMidYMax slice" aria-hidden>
      <defs>
        <clipPath id={`${uid}-trunk-band`}><rect x={sceneX0 - 400} y={SCENE.ground - 150} width={sceneW + 800} height="150" /></clipPath>
      </defs>
      <g className="banyan-pan" style={panStyle} clipPath={`url(#${uid}-trunk-band)`}>
        <path d={trunk} fill={`url(#${ids.trunk})`} />
        <path d={`M256 ${SCENE.ground - 200} C 262 ${SCENE.ground - 120}, 254 ${SCENE.ground - 60}, 232 ${SCENE.ground}`} className="banyan-bark" />
        <path d={`M306 ${SCENE.ground - 200} C 302 ${SCENE.ground - 120}, 308 ${SCENE.ground - 60}, 328 ${SCENE.ground}`} className="banyan-bark" />
        <path d={earth.far} fill={`url(#${ids.bank})`} />
        <path d={earth.rim} fill="none" stroke={`url(#${ids.ridge})`} strokeWidth="1.4" strokeLinecap="round" />
        <path d={earth.near} className="banyan-earth" />
      </g>
    </svg>
  );
  /** A tap on Ember: the page hangs its card above its head — the hit
      ellipse's own box, in the scene box's pixels (the way a light's tag is
      anchored), so the card lands where Ember is drawn right now. */
  const tapEmber = (target: SVGGraphicsElement): void => {
    // Standing by the letters, Ember is pointing at them: a tap on it opens them (2026-09-29 拍板).
    if (calling) { track('ui.clicked', { name: 'tree_ember_letter', state: 'ember' }); onLettersTap?.(); return; }
    if (!onEmberTap) return;
    track('ui.clicked', { name: 'tree_ember_tap' });
    const box = target.getBoundingClientRect();
    const host = svgRef.current?.parentElement?.getBoundingClientRect();
    onEmberTap({
      x: box.left + box.width / 2 - (host?.left || 0),
      y: box.top - (host?.top || 0) - 6,
    });
  };

  return (
    <div className={`banyan-scene${clock ? ' is-clock' : ''}${className ? ` ${className}` : ''}`} style={arrival?.tempo ? { ['--banyan-tempo' as string]: String(arrival.tempo) } : undefined}>
      {/* The homepage's sky (clock): the box's background is its colour; here the sun and its clouds by day, the moon
          and its stars at night — far off, so they do not pan. Twinkling here repaints this layer only. */}
      {clock ? (
        <svg className="banyan-sky-layer" viewBox={viewBox} preserveAspectRatio="xMidYMax slice" aria-hidden>
          <defs>
            <radialGradient id={`${uid}-sunglow`}>
              <stop offset="0" stopColor="var(--banyan-orb-glow)" stopOpacity=".7" /><stop offset="1" stopColor="var(--banyan-orb-glow)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g className="banyan-day">
            <circle cx={orb.x} cy={orb.y} r="58" fill={`url(#${uid}-sunglow)`} />
            <circle className="banyan-sun" cx={orb.x} cy={orb.y} r="19" />
            <circle className="banyan-sun-core" cx={orb.x - 2} cy={orb.y - 2} r="13" />
            {CLOUDS.map(([x, y, k]) => (
              <g key={`${x}-${y}`} className="banyan-cloud">
                <circle cx={x - 17 * k} cy={y + 9 * k} r={11 * k} />
                <circle cx={x} cy={y} r={15 * k} />
                <circle cx={x + 18 * k} cy={y + 8 * k} r={11 * k} />
                <rect x={x - 28 * k} y={y + 8 * k} width={57 * k} height={12 * k} rx={6 * k} />
              </g>
            ))}
          </g>
          <g className="banyan-night">
            {STARS.map(([x, y, r], i) => <circle key={i} className={`banyan-star${i % 2 ? ' is-late' : ''}`} cx={x} cy={y} r={r} />)}
            <circle cx={orb.x} cy={orb.y} r="58" fill={`url(#${uid}-sunglow)`} />
            <path className="banyan-moon" d={MOON_PATH} transform={`translate(${orb.x - 21} ${orb.y - 21}) scale(1.75)`} />
          </g>
        </svg>
      ) : null}
      {/* The still tree: painted once; repainted only when the pan or the viewer changes. */}
      <svg className="banyan-still" viewBox={viewBox} preserveAspectRatio="xMidYMax slice" aria-hidden>
        <defs>
          <linearGradient id={ids.sky} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--banyan-night)" />
            <stop offset=".56" stopColor="var(--banyan-dusk)" />
            <stop offset=".9" stopColor="var(--banyan-horizon)" />
            <stop offset="1" stopColor="#5c3224" />
          </linearGradient>
          <linearGradient id={ids.rim} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--banyan-lamp)" stopOpacity=".25" />
            <stop offset=".5" stopColor="var(--banyan-rim)" />
            <stop offset="1" stopColor="var(--banyan-lamp)" stopOpacity=".25" />
          </linearGradient>
          <linearGradient id={ids.trunk} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--banyan-trunk-1)" /><stop offset=".55" stopColor="var(--banyan-trunk-2)" /><stop offset="1" stopColor="var(--banyan-trunk-3)" />
          </linearGradient>
          <radialGradient id={ids.hz} cx=".5" cy="1" r=".9">
            <stop offset="0" stopColor="#c2703a" stopOpacity=".55" /><stop offset="1" stopColor="#c2703a" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={ids.seat} cx=".5" cy=".6" r=".5">
            <stop offset="0" stopColor="var(--banyan-seat)" stopOpacity=".5" /><stop offset=".55" stopColor="var(--banyan-seat)" stopOpacity=".16" /><stop offset="1" stopColor="var(--banyan-seat)" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={ids.bank} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--banyan-bank-top)" /><stop offset="1" stopColor="var(--banyan-bank-bottom)" />
          </linearGradient>
          {clock ? (
            <>
              <linearGradient id={`${uid}-hill-far`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="var(--banyan-hill-far-top)" /><stop offset=".55" stopColor="var(--banyan-hill-far)" />
              </linearGradient>
              <linearGradient id={`${uid}-hill-near`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="var(--banyan-hill-near-top)" /><stop offset=".55" stopColor="var(--banyan-hill-near)" />
              </linearGradient>
            </>
          ) : null}
          <linearGradient id={ids.ridge} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--banyan-ridge)" stopOpacity="0" /><stop offset=".5" stopColor="var(--banyan-ridge)" stopOpacity=".55" /><stop offset="1" stopColor="var(--banyan-ridge)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* The sky — its own layer, so a page laying the scene over something
            it must not hide (the thread's arrival, EmberArrival.css) can leave
            the sky out and keep the tree. The homepage's is the box's own. */}
        {clock ? null : (
          <>
            <rect className="banyan-sky" x={sceneX0} width={sceneW} height={SCENE.h} fill={`url(#${ids.sky})`} />
            <rect className="banyan-sky" x={sceneX0} y={SCENE.ground - 160} width={sceneW} height="160" fill={`url(#${ids.hz})`} />
          </>
        )}
        <g className="banyan-pan" style={panStyle}>
          {/* The hills and their cabins (clock), behind the roots; the path climbs the near hill to the near cabin, its
              foot under the near bank. */}
          {hills ? (
            <>
              <HillShape d={hills.far} fill={`url(#${uid}-hill-far)`} />
              {cabins.filter((c) => !c.near).map((c) => <CabinShape key={c.x} c={c} />)}
              <HillShape d={hills.near} fill={`url(#${uid}-hill-near)`} />
              {walk ? <path d={walk} className="banyan-walk" /> : null}
              {cabins.filter((c) => c.near).map((c) => <CabinShape key={c.x} c={c} />)}
            </>
          ) : null}
          {/* The aerial roots — not on the homepage's scene (clock, 2026-10-27 founder): across the hills' ridges they drew a net. */}
          {clock ? null : foliage.roots.map((r, i) => <path key={i} d={root(r)} className="banyan-root" strokeWidth={r.w.toFixed(1)} />)}
          <path d={trunk} fill={`url(#${ids.trunk})`} />
          <path d={`M256 ${SCENE.ground - 200} C 262 ${SCENE.ground - 120}, 254 ${SCENE.ground - 60}, 232 ${SCENE.ground}`} className="banyan-bark" />
          <path d={`M306 ${SCENE.ground - 200} C 302 ${SCENE.ground - 120}, 308 ${SCENE.ground - 60}, 328 ${SCENE.ground}`} className="banyan-bark" />
          <g>{foliage.leaves.map((l, i) => <ellipse key={i} cx={sx(l.x).toFixed(1)} cy={l.y.toFixed(1)} rx={l.rx.toFixed(1)} ry={l.ry.toFixed(1)} fill={SHADES[l.shade]} opacity=".95" />)}</g>
          {/* The earth (2026-09-07 拍板): the far bank catches the last of the
              horizon along its ridge; the near bank is the ground band's own
              colour, so the band under the scene is this earth going on. The
              roots' and the trunk's feet go into it — the figure and the
              letters sit on it from their own layers above. */}
          <path d={earth.far} fill={`url(#${ids.bank})`} />
          <path d={earth.rim} fill="none" stroke={`url(#${ids.ridge})`} strokeWidth="1.4" strokeLinecap="round" />
          <path d={earth.near} className="banyan-earth" />
        </g>
        {/* The last of the dusk falls on the seat under the trunk (2026-09-06 走查):
            the figure is drawn in the trunk's own ink, and against the trunk it
            was a dark shape on dark — a lit seat behind it is what makes a
            silhouette read. */}
        {animal ? <ellipse className="banyan-seat" cx={SCENE.viewerX} cy={SCENE.ground - 52} rx="122" ry="94" fill={`url(#${ids.seat})`} /> : null}
      </svg>

      {/* Ember and the trunk it hides behind (拍板 2026-09-14). The trunk, its
          two root lines and the earth at its foot are painted again on a thin
          static layer — only the lower band, well under the canopy — so that
          Ember, on its own layer, can stand behind the trunk or in front of it
          by the order of the two layers alone: walking behind the trunk is just
          walking. The trunk layer changes sides only while Ember stands clear
          of the trunk, where the swap paints nothing different. Ember's moves
          are one CSS transition on its group; the drawing is never re-rendered. */}
      {emberOn && emberPose.front ? trunkLayer : null}
      {emberOn ? (
        <svg className="banyan-ember-layer" viewBox={viewBox} preserveAspectRatio="xMidYMax slice" aria-hidden>
          <g className="banyan-pan" style={panStyle}>
            <g
              className={`banyan-ember${emberPose.walking ? ' is-walking' : ''}${emberPose.quick ? ' is-quick' : ''}${arrival?.stage === 'speaking' ? ' is-speaking' : ''}`}
              style={{ transform: `translate(${emberAt.x.toFixed(1)}px, ${emberAt.y.toFixed(1)}px) scale(${emberAt.scale.toFixed(3)})` }}
            >
              <g ref={emberHost} className="banyan-ember-hop" dangerouslySetInnerHTML={{ __html: emberHtml }} />
              {firefly > 0 ? <circle key={firefly} className="banyan-ember-firefly" r="1.6" fill="#ffe6a8" /> : null}
            </g>
          </g>
        </svg>
      ) : null}
      {emberOn && !emberPose.front ? trunkLayer : null}

      {/* The figure under the tree, on its own layer: when it moves (its head
          wags in the ceremony, it reacts to a poke) only this small drawing is
          re-rastered, never the tree behind it. Its rim gradient is the still
          layer's — url(#id) reaches across the document. */}
      {animal ? (
        <svg className="banyan-you-layer" viewBox={viewBox} preserveAspectRatio="xMidYMax slice" aria-hidden>
          <AnimalBack animal={animal} x={SCENE.viewerX} y={SCENE.ground} size={figure} rimId={ids.rim} speaking={arrival?.stage === 'speaking'} reaction={reaction?.id ?? null} />
          {reaction?.say ? <PokeWords key={poke?.seq} text={reaction.say} mouth={{ x: mouthX, y: mouthY }} zoom={zoom} ms={reaction.ms} /> : null}
          {reaction?.mark ? (
            <g key={poke?.seq} className={`banyan-poke-marks is-${reaction.id}`} style={{ ['--poke-ms' as string]: `${reaction.ms}ms` }}>
              <text className="banyan-poke-mark" x={(crownX + 12).toFixed(1)} y={(crownY - 14).toFixed(1)} style={{ fontSize: `${(22 * zoom).toFixed(2)}px` }}>{reaction.mark}</text>
              {reaction.id === 'hum' ? <text className="banyan-poke-mark" x={(crownX + 30).toFixed(1)} y={(crownY - 28).toFixed(1)} style={{ fontSize: `${(16 * zoom).toFixed(2)}px` }}>{reaction.mark}</text> : null}
            </g>
          ) : null}
        </svg>
      ) : null}

      {/* The live layer: everything that moves, and every pointer. */}
      <svg
        ref={svgRef}
        className="banyan-live"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMax slice"
        aria-hidden
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => {
          if (drag.current && !drag.current.moved) {
            // A press that ended on nothing — sky, trunk, ground (2026-09-08).
            // With a tag open it is the way to close it (state=close); with
            // nothing open it is a hand on the tree that found no light
            // (state=miss) — the tap the declarative hook never saw, since no
            // <button> is under it. The lights, the figure and the letters
            // stop their own pointerup before it reaches here.
            if (onLightTap) track('ui.clicked', { name: 'tree_background_tap', state: highlightId ? 'close' : 'miss' });
            onBackgroundTap?.();
          }
          void e;
          endDrag();
        }}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
      >
        <defs>
          <radialGradient id={ids.glow}>
            <stop offset="0" stopColor="var(--banyan-lamp)" stopOpacity=".9" />
            <stop offset=".5" stopColor="var(--banyan-lamp)" stopOpacity=".25" />
            <stop offset="1" stopColor="var(--banyan-lamp)" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={ids.rise}>
            <stop offset="0" stopColor="#ffd9a3" stopOpacity=".95" />
            <stop offset=".35" stopColor="var(--eh-brand)" stopOpacity=".45" />
            <stop offset="1" stopColor="var(--eh-brand)" stopOpacity="0" />
          </radialGradient>
          {clock ? (
            <radialGradient id={`${uid}-window`}>
              <stop offset="0" stopColor="var(--banyan-window)" stopOpacity=".5" /><stop offset="1" stopColor="var(--banyan-window)" stopOpacity="0" />
            </radialGradient>
          ) : null}
        </defs>
        <g className="banyan-pan" style={panStyle}>
          {/* The cabins' life (clock): smoke from each chimney by day, the light round each window at night. */}
          {cabins.map((c) => (
            <g key={c.x} className="banyan-cabin-life" style={{ ['--k' as string]: c.s.toFixed(2) }}>
              <g className="banyan-smoke">
                {[0, 1, 2].map((i) => <circle key={i} cx={c.chimney.x.toFixed(1)} cy={c.chimney.y.toFixed(1)} r={(c.s * (3.2 + i)).toFixed(1)} style={{ animationDelay: `${(i * 1.5 + c.x / 400).toFixed(2)}s` }} />)}
              </g>
              <circle className="banyan-window-glow" cx={c.window.x.toFixed(1)} cy={c.window.y.toFixed(1)} r={(c.s * 22).toFixed(1)} fill={`url(#${uid}-window)`} />
            </g>
          ))}
          <g className="banyan-fireflies">
            {foliage.fireflies.map((f, i) => (
              <circle key={i} className="banyan-firefly" style={{ animationDelay: `${f.delay.toFixed(2)}s`, animationDuration: `${f.dur.toFixed(2)}s` }} cx={sx(f.x).toFixed(1)} cy={f.y.toFixed(1)} r="1.1" fill="#e9d59a" opacity=".5" />
            ))}
          </g>
          <g className="banyan-lights">
            {leaving.map((p) => (
              <g key={`leave-${p.node.id}`} className="banyan-light is-leaving" style={{ transform: `translate(${sx(p.x).toFixed(1)}px, ${p.y.toFixed(1)}px)` }}>
                <circle r="18" fill={`url(#${ids.glow})`} opacity=".3" />
                <circle className="banyan-light-core" r="3" fill="var(--banyan-lamp-core)" opacity=".6" />
              </g>
            ))}
            {placed.map((p, i) => {
              const isHl = highlight?.node.id === p.node.id;
              const isMine = p.node.mine === true;
              let near = 1;
              if (highlight && !isHl) {
                const d = Math.hypot(highlight.x - p.x, highlight.y - p.y);
                near = Math.max(0.3, Math.min(1, 1.15 - d / 260));
              }
              const r = isHl ? 5.6 : 3.4;
              const a = (isHl ? 1 : lampAlpha(p.node.people_count)) * near;
              const isPressed = p.node.id === pressId;
              return (
                <g
                  key={p.node.id}
                  className={`banyan-light is-entering${isHl ? ' is-open' : ''}${isPressed ? ' is-pressed' : ''}${isMine ? ' is-mine' : ''}${p.node.focus ? ' is-focus' : ''}`}
                  style={{ transform: `translate(${sx(p.x).toFixed(1)}px, ${p.y.toFixed(1)}px)`, animationDelay: `${((i * 0.37) % 5).toFixed(2)}s` }}
                  role={onLightTap ? 'button' : undefined}
                  tabIndex={onLightTap ? 0 : undefined}
                  onPointerUp={(e) => tap(p, e)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLight(p.node, { x: 0, y: 0 }); } }}
                >
                  <circle className="banyan-light-glow" r={(r * 6).toFixed(1)} fill={`url(#${ids.glow})`} opacity={(a * 0.55).toFixed(2)} />
                  {/* a generous invisible hit target: a light is small, a thumb is not */}
                  <circle r="22" fill="transparent" />
                  {isMine ? <circle className="banyan-light-ring" r={(r + 4).toFixed(1)} fill="none" stroke="var(--banyan-lamp)" strokeWidth="1.2" opacity={(a * 0.8).toFixed(2)} /> : null}
                  <circle className="banyan-light-core" r={r.toFixed(1)} fill="var(--banyan-lamp-core)" opacity={a.toFixed(2)} />
                  {isPressed ? (
                    <>
                      <circle className="banyan-light-press" r="4" fill="none" stroke="var(--banyan-lamp)" strokeWidth="1.4" />
                      <circle className="banyan-light-press banyan-light-press-2" r="4" fill="none" stroke="var(--banyan-lamp)" strokeWidth="1" />
                    </>
                  ) : null}
                </g>
              );
            })}
          </g>
          {arrival ? (() => {
            // The arriving light. While the teller is still speaking, a speech
            // bubble stands at their mouth — its tail on the side of the head,
            // three rows of scribble lighting up phrase by phrase (three
            // phrases) while the light gathers inside it, the head wagging the
            // whole time: what is being said is becoming a light. Then the
            // bubble bursts and the spark leaves from where the light stood,
            // climbs, and curves over onto its spot, where the light swells and
            // stays, ringed as the viewer's own. The gathered light and the
            // spark are two drawings of one thing, swapped in the same frame at
            // the same point.
            const { x, y } = arrival;
            const path = `M${bubbleX} ${bubbleY} C ${bubbleX + (x - bubbleX) * 0.05} ${bubbleY - (bubbleY - y) * 0.75}, ${x + (x - bubbleX) * 0.5} ${y - 60}, ${x} ${y}`;
            const bw = 96;
            const bh = 62;
            const bubble = bubblePath(bubbleX - bw / 2, bubbleY - bh / 2, bw, bh, 18, { x: mouthX + 6, y: mouthY }, 16);
            const wordY = bubbleY - 12;
            return (
              <g className={`banyan-arrival is-${arrival.stage}`}>
                <g className="banyan-arrival-bubble">
                  <path d={bubble} fill="rgba(23, 18, 42, 0.72)" stroke="var(--banyan-lamp)" strokeWidth="2.2" strokeLinejoin="round" />
                  <g className="banyan-arrival-words" fill="none" stroke="var(--banyan-lamp)" strokeWidth="2.4" strokeLinecap="round">
                    <path d={`M${bubbleX - 30} ${wordY} q5 -5 10 0 t10 0 t10 0 t10 0 t10 0`} />
                    <path d={`M${bubbleX - 30} ${wordY + 12} q5 -5 10 0 t10 0 t10 0 t10 0`} />
                    <path d={`M${bubbleX - 30} ${wordY + 24} q5 -5 10 0 t10 0 t10 0 t10 0 t10 0 t10 0`} />
                  </g>
                  {/* placed by the outer group: the inner one is what the CSS scales */}
                  <g transform={`translate(${bubbleX}, ${bubbleY})`}>
                    <g className="banyan-arrival-seed">
                      <circle r="34" fill={`url(#${ids.rise})`} opacity=".9" />
                      <circle r="4.5" fill="var(--banyan-lamp-core)" />
                    </g>
                  </g>
                </g>
                {/* Ember's turn (拍板 2026-09-14): a paper bubble at its mouth
                    for the middle of the speaking beat — the two of them take
                    turns; the light gathers only while you speak. */}
                {(() => {
                  const ex = sx(EMBER_CEREMONY_POSE.x);
                  const mx = ex + 12;
                  const my = SCENE.ground - 44;
                  const bx = ex - 52;
                  const by = SCENE.ground - 112;
                  const d = `M ${bx - 37} ${by - 21} h 74 a 12 12 0 0 1 12 12 v 22 a 12 12 0 0 1 -12 12 h -6 L ${mx} ${my} L ${bx + 24} ${by + 25} h -61 a 12 12 0 0 1 -12 -12 v -22 a 12 12 0 0 1 12 -12 z`;
                  return (
                    <g className="banyan-arrival-reply">
                      <path d={d} fill="#fff4e6" stroke="rgba(124, 83, 52, 0.35)" strokeWidth="1.4" strokeLinejoin="round" />
                      <g fill="none" stroke="#c9895f" strokeWidth="2.2" strokeLinecap="round">
                        <path d={`M${bx - 24} ${by - 5} q5 -5 10 0 t10 0 t10 0 t10 0`} />
                        <path d={`M${bx - 24} ${by + 7} q5 -5 10 0 t10 0 t10 0`} />
                      </g>
                    </g>
                  );
                })()}
                <g className="banyan-arrival-spark">
                  <circle r="34" fill={`url(#${ids.rise})`} opacity=".9" />
                  <circle r="4.5" fill="var(--banyan-lamp-core)" />
                  <animateMotion ref={flightRef} path={path} begin="indefinite" dur={`${(1.5 * (arrival.tempo ?? 1)).toFixed(2)}s`} fill="freeze" calcMode="spline" keySplines="0.35 0 0.25 1" keyTimes="0;1" keyPoints="0;1" />
                </g>
                <g className="banyan-light banyan-arrival-light is-mine" style={{ ['--ax' as string]: `${x.toFixed(1)}px`, ['--ay' as string]: `${y.toFixed(1)}px`, transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)` }}>
                  <circle r="21" fill={`url(#${ids.glow})`} opacity=".55" />
                  <circle className="banyan-light-ring" r="7.4" fill="none" stroke="var(--banyan-lamp)" strokeWidth="1.2" opacity=".8" />
                  <circle className="banyan-light-core" r="3.4" fill="var(--banyan-lamp-core)" />
                </g>
              </g>
            );
          })() : null}
        </g>
        {/* Ember's hit area, following it on the same transition (the drawing
            is on its own layer below, which takes no pointers). A tap on it is
            the card, not the sky; a drag that ends here is still a drag. */}
        {emberOn && !arrival && onEmberTap ? (
          <g className={`banyan-ember-hit${emberPose.walking ? ' is-walking' : ''}${emberPose.quick ? ' is-quick' : ''}`} style={{ transform: `translate(${emberAt.x.toFixed(1)}px, ${emberAt.y.toFixed(1)}px)` }}>
            <ellipse
              cx="0"
              cy={(-0.6 * emberAt.w).toFixed(1)}
              rx={(0.55 * emberAt.w).toFixed(1)}
              ry={(0.66 * emberAt.w).toFixed(1)}
              fill="transparent"
              className="banyan-you-hit"
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => { if (drag.current?.moved) return; e.stopPropagation(); drag.current = null; tapEmber(e.currentTarget); }}
            />
          </g>
        ) : null}
        {/* The figure's hit area (the figure itself is on the layer below,
            which takes no pointers): a tap on the person under the tree is a
            poke, not the sky. A drag that ends here is still a drag. */}
        {animal && !arrival ? (
          <ellipse
            className="banyan-you-hit"
            cx={SCENE.viewerX}
            cy={(SCENE.ground - figure * 0.32).toFixed(1)}
            rx={(figure * 0.36).toFixed(1)}
            ry={(figure * 0.45).toFixed(1)}
            fill="transparent"
            onPointerUp={(e) => { if (drag.current?.moved) return; e.stopPropagation(); drag.current = null; pokeYou(); }}
          />
        ) : null}
        {/* The letters (§4.9): Ember's envelopes on the ground at the figure's
            side — the figure is the viewer, so what Ember has to say arrives
            addressed to them, never spoken from their own mouth. The newest
            three drawn, fanned, each on top of the one before it (so a letter
            that has just arrived is never hidden under older ones); the paper
            says how many there are. A sealed one glows (opacity only, like the
            lights); an opened one lies plain. On the top envelope's corner the
            count of sealed letters, in the top bar's red (2026-09-08) — the
            pile is read at a glance as "N unread", like the badge.
            Not during the ceremony — the ground is the ceremony's then.
            The pile opens on click, not pointerup (2026-09-07): a finger's
            pointerup mounted the sheet, and the browser's click, synthesised
            ~30ms later, landed on the sheet's backdrop and dismissed it — the
            sheet opened and vanished under the same tap. The pointer handlers
            only keep the press from starting a pan or reading as a background
            tap; with no pan begun here, the click is the tap. */}
        {animal && !arrival && letters.length > 0 ? (() => {
          const { w, h, x0, y0 } = pile;
          const shown = letters.slice(-3);
          const unread = letters.filter((l) => l.sealed).length;
          const badgeX = pile.badge.x;
          const badgeY = pile.badge.y;
          return (
            <g
              className="banyan-letters"
              data-log-seen="knock_letters"
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              onClick={() => {
                track('ui.clicked', { name: 'knock_letters' });
                onLettersTap?.();
              }}
            >
              {shown.map((l, i) => {
                const x = x0 + i * 14 - (shown.length - 1) * 5;
                const y = y0 - i * 6;
                const rot = i % 2 ? 9 : -8;
                return (
                  <g
                    key={l.id}
                    className={`banyan-letter ${l.sealed ? 'is-sealed' : 'is-opened'}${l.fly ? ' is-flying' : ''}`}
                    style={{
                      ['--lx' as string]: `${x.toFixed(1)}px`,
                      ['--ly' as string]: `${y.toFixed(1)}px`,
                      ['--lr' as string]: `${rot}deg`,
                      ['--li' as string]: String(i),
                      transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) rotate(${rot}deg)`,
                    }}
                  >
                    {l.sealed ? (
                      <>
                        <ellipse className="banyan-letter-glow is-far" cx={(w / 2).toFixed(1)} cy={(h / 2).toFixed(1)} rx={(w * 0.72).toFixed(1)} ry={(h * 0.82).toFixed(1)} fill="var(--banyan-lamp)" />
                        <ellipse className="banyan-letter-glow" cx={(w / 2).toFixed(1)} cy={(h / 2).toFixed(1)} rx={(w * 0.58).toFixed(1)} ry={(h * 0.66).toFixed(1)} fill="var(--banyan-lamp)" />
                      </>
                    ) : null}
                    <g transform={`scale(${(w / 40).toFixed(3)})`}><LetterShapes sealed={l.sealed} /></g>
                  </g>
                );
              })}
              {unread > 0 ? (
                <g className="banyan-letters-badge" aria-hidden>
                  <circle cx={badgeX.toFixed(1)} cy={badgeY.toFixed(1)} r="9" />
                  <text x={badgeX.toFixed(1)} y={(badgeY + 0.5).toFixed(1)} textAnchor="middle" dominantBaseline="central">{unread}</text>
                </g>
              ) : null}
              <rect className="banyan-letters-hit" x={(x0 - 14).toFixed(1)} y={(y0 - 18).toFixed(1)} width={(w + 42).toFixed(1)} height={(h + 30).toFixed(1)} fill="transparent" />
            </g>
          );
        })() : null}
      </svg>
      {signpost ? (() => {
        // Under the tags in paint order: a low light's tag may hang over it.
        const x = Math.max(sx(SIGNPOST_X), view.x0 + SIGNPOST_HALF_PX * zoom);
        const at = toScreen({ x: x + pan, y: SCENE.ground - 3, w: 0, side: 'below' });
        return <div className="banyan-signpost" style={{ left: `${at.x.toFixed(1)}px`, top: `${at.y.toFixed(1)}px` }}>{signpost}</div>;
      })() : null}
      {/* The tags are HTML over the scene, not <foreignObject> inside it
          (2026-09-08): iOS Safari before 17 paints foreignObject content at
          1:1, ignoring the viewBox scale — on a 375px phone the tag came out
          1.5× its size and ran off the right edge (the place page's band
          never had the bug: it already hung the tag this way, PlaceCanopy, until 2026-09-21).
          layoutLabel still works in scene units; toScreen turns its box into
          pixels inside .banyan-scene, whose box is the live SVG's. */}
      {fadingTag && fadingTag.node.id !== highlightId ? (() => {
        // The handover: the tag that just went out, fading where it hung.
        const box = toScreen(layoutLabel({
          cx: sx(fadingTag.x) + pan,
          cy: fadingTag.y,
          h: (fadingTag.h || 96) * zoom,
          zoom,
          view,
          bottom: SCENE.h,
          others: placed.filter((p) => p.node.id !== fadingTag.node.id).map((p) => ({ x: sx(p.x) + pan, y: p.y })),
        }));
        return (
          <div
            className="banyan-label is-out"
            key={`out-${fadingTag.node.id}`}
            style={{ left: `${box.x.toFixed(1)}px`, top: `${box.y.toFixed(1)}px`, width: `${box.w.toFixed(1)}px` }}
          >
            <div className={`banyan-label-text is-${box.side} is-out`} aria-hidden="true">
              {fadingTag.hint}
              <div className="banyan-label-title"><span>{fadingTag.node.title}</span></div>
              <div className="banyan-label-people"><span><PeopleLine count={fadingTag.node.people_count} /></span></div>
              {fadingTag.actions}
            </div>
          </div>
        );
      })() : null}
      {highlight ? (() => {
        // The open light's name hangs on the light like a tag on a lantern —
        // the canopy stays visible and every other light stays tappable
        // (§4.17, 2026-09-05: no paper over the tree). Its doors are the
        // tag's third row, so everything about a light is at the light and
        // the ground below keeps its one door. Drawn outside the panned group
        // in screen pixels; its place follows the light, held inside the
        // visible strip and below the nav that floats over the canopy's top
        // (2026-09-05 walkthrough: a high light's tag printed across How it
        // works / Blog and its door sat under the header, unclickable).
        // 2026-09-06: it hangs BESIDE the light — layoutLabel picks the side
        // with room and the fewer lights under it, and the words hug the
        // light from that side; only when neither side holds a tag does it
        // fall back under the light. A tap on the tag is neither a drag nor
        // the sky: the tag is a sibling of the live SVG, so nothing on it
        // reaches the scene's pointer handlers.
        // Keyed by the light (2026-09-07): the tag fades IN on every
        // handover, which a reused element would have skipped.
        const box = toScreen(layoutLabel({
          cx: sx(highlight.x) + pan,
          cy: highlight.y,
          h: (labelH || 96) * zoom,
          zoom,
          view,
          bottom: SCENE.h,
          others: placed.filter((p) => p.node.id !== highlight.node.id).map((p) => ({ x: sx(p.x) + pan, y: p.y })),
        }));
        return (
          <div
            className="banyan-label"
            key={highlight.node.id}
            style={{ left: `${box.x.toFixed(1)}px`, top: `${box.y.toFixed(1)}px`, width: `${box.w.toFixed(1)}px` }}
          >
            <div ref={labelRef} className={`banyan-label-text is-${box.side}`}>
              {labelHint}
              <div className="banyan-label-title"><span>{highlight.node.title}</span></div>
              <div className="banyan-label-people"><span><PeopleLine count={highlight.node.people_count} /></span></div>
              {labelActions}
            </div>
          </div>
        );
      })() : null}
      {calling && arrivedAtLetters ? (() => {
        // Ember's one line by the letters (2026-09-29 拍板), hung above its head
        // in screen pixels like the tags and its own card: the words are the
        // door — a tap opens the letters sheet as a tap on the pile does.
        const at = toScreen({ x: perch.x + pan, y: perch.y - perch.w * 1.22, w: 0, side: 'below' });
        return (
          <button
            type="button"
            className="banyan-ember-say"
            style={{ left: `${at.x.toFixed(1)}px`, top: `${at.y.toFixed(1)}px` }}
            data-log-seen="tree_ember_letter"
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            data-log-state={invited ? 'invite' : 'letter'}
            onClick={() => { track('ui.clicked', { name: 'tree_ember_letter', state: 'line' }); onLettersTap?.(); }}
          >
            {invited ? 'You’re invited to a room.' : 'You have a letter.'}<small>Tap to open it.</small>
          </button>
        );
      })() : null}
    </div>
  );
}

/** Rendered only when its props change: the Result page under the ceremony
    re-renders every frame while the draft is written a grapheme at a time, and
    the scene must not be reconciled with it. */
export const BanyanScene = memo(BanyanSceneImpl);
